import React, { useEffect, useState } from 'react';
import { Camera } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useUpload } from '../lib/useUpload';
import type { User } from '../types';
import { Spinner, Alert, UploadProgress, inputClass, labelClass, primaryBtn } from './ui';

export const isStaffProfileComplete = (p: { bank_name: string | null; bank_account: string | null; account_name: string | null; next_of_kin_name: string | null; next_of_kin_phone: string | null } | null | undefined) =>
  !!p && !!(p.next_of_kin_name && p.next_of_kin_phone && p.bank_name && p.bank_account && p.account_name);

interface ProfileData extends User {
  profile: {
    id: number;
    role_title: string | null;
    estate: string | null;
    bank_name: string | null;
    bank_account: string | null;
    account_name: string | null;
    next_of_kin_name: string | null;
    next_of_kin_phone: string | null;
    next_of_kin_relationship: string | null;
  } | null;
}

export default function Profile() {
  const { setUser } = useAuth();
  const [data, setData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [estate, setEstate] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [accountName, setAccountName] = useState('');
  const [nextOfKinName, setNextOfKinName] = useState('');
  const [nextOfKinPhone, setNextOfKinPhone] = useState('');
  const [nextOfKinRelationship, setNextOfKinRelationship] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const avatarUpload = useUpload();
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    api
      .get<ProfileData>('/auth/profile')
      .then((d) => {
        setData(d);
        setName(d.name);
        setPhone(d.phone || '');
        setRoleTitle(d.profile?.role_title || '');
        setEstate(d.profile?.estate || '');
        setBankName(d.profile?.bank_name || '');
        setBankAccount(d.profile?.bank_account || '');
        setAccountName(d.profile?.account_name || '');
        setNextOfKinName(d.profile?.next_of_kin_name || '');
        setNextOfKinPhone(d.profile?.next_of_kin_phone || '');
        setNextOfKinRelationship(d.profile?.next_of_kin_relationship || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const res = await api.put<ProfileData>('/auth/profile', {
        name,
        phone,
        role_title: roleTitle || null,
        estate: data?.role === 'staff' ? undefined : (estate || null),
        bank_name: bankName || null,
        bank_account: bankAccount || null,
        account_name: accountName || null,
        next_of_kin_name: nextOfKinName || null,
        next_of_kin_phone: nextOfKinPhone || null,
        next_of_kin_relationship: nextOfKinRelationship || null,
      });
      setData(res);
      setUser?.(res);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    avatarUpload.reset();
    setError('');
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      const res = await avatarUpload.run<ProfileData>('/auth/avatar', fd);
      setData((prev) => ({ ...(prev as ProfileData), ...res, profile: res.profile ?? prev?.profile ?? null }));
      setUser?.(res);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwSaving(true);
    setPwMsg(null);
    try {
      await api.post('/auth/change-password', {
        current_password: pwCurrent,
        new_password: pwNew,
      });
      setPwMsg({ ok: true, text: 'Password changed successfully.' });
      setPwCurrent('');
      setPwNew('');
    } catch (err) {
      setPwMsg({ ok: false, text: (err as Error).message });
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) return <Spinner label="Loading profile..." />;
  if (!data) return <Alert>Could not load profile.</Alert>;

  const complete = isStaffProfileComplete(data.profile);
  const isStaff = data.role === 'staff';

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold mb-8">My Profile</h1>

      {error && <Alert>{error}</Alert>}
      {saved && <Alert type="success">Profile updated successfully.</Alert>}
      {data.profile && !complete && (
        <Alert>Please complete your salary and next of kin details below. Your manager may need them for payroll and emergencies.</Alert>
      )}

      <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
        <div className="flex items-center space-x-4">
          <div className="relative">
            {data.image_url ? (
              <img src={data.image_url} alt={data.name} className="w-20 h-20 rounded-full object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-primary text-black flex items-center justify-center text-xl font-bold">
                {data.name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()}
              </div>
            )}
            <label className="absolute -bottom-1 -right-1 bg-white rounded-full p-1.5 shadow cursor-pointer border border-gray-200">
              <Camera className="h-4 w-4 text-gray-600" />
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleAvatar}
              />
            </label>
          </div>
          <div>
            <p className="text-lg font-semibold">{data.name}</p>
            <p className="text-sm text-gray-500 capitalize">{data.role}</p>
            {data.profile?.role_title && (
              <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full bg-primary/10 text-primary-dark text-xs font-medium capitalize">
                {data.profile.role_title}
              </span>
            )}
          </div>
          <UploadProgress
            percent={avatarUpload.percent}
            phase={avatarUpload.phase}
            error={avatarUpload.error}
            onCancel={avatarUpload.cancel}
          />
        </div>
      </div>

      <form onSubmit={handleSave} className="bg-white p-6 rounded-lg shadow-lg space-y-4">
        <h2 className="text-lg font-semibold">Account Details</h2>
        <div>
          <label className={labelClass}>Email</label>
          <input className={`${inputClass} bg-gray-50`} value={data.email} disabled />
        </div>
        <div>
          <label className={labelClass}>Full Name</label>
          <input className={`${inputClass} ${isStaff ? 'bg-gray-50' : ''}`} value={name} onChange={(e) => setName(e.target.value)} disabled={isStaff} required />
        </div>
        <div>
          <label className={labelClass}>Phone</label>
          <input className={`${inputClass} ${isStaff ? 'bg-gray-50' : ''}`} value={phone} onChange={(e) => setPhone(e.target.value)} disabled={isStaff} />
        </div>
        {isStaff && (
          <p className="text-xs text-gray-400 -mt-2">Full name, phone number and email cannot be changed. Contact your administrator to update them.</p>
        )}

        {data.profile && (
          <>
            <div className="border-t border-gray-100 pt-4">
              <h2 className="text-lg font-semibold mb-4">Work Details</h2>
              <div>
                <label className={labelClass}>Estate / Facility Worked In</label>
                <input className={`${inputClass} ${isStaff ? 'bg-gray-50' : ''}`} value={estate} onChange={(e) => setEstate(e.target.value)} disabled={isStaff} placeholder="e.g. Pearl Garden Estate" />
                {isStaff ? (
                  <p className="text-xs text-gray-400 mt-1">Your estate is assigned by your administrator and cannot be changed.</p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1">The estate or facility you work in.</p>
                )}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <h2 className="text-lg font-semibold mb-4">Salary & Bank Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Bank Name</label>
                  <input className={inputClass} value={bankName} onChange={(e) => setBankName(e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>Account Number</label>
                  <input className={inputClass} value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>Account Name</label>
                  <input className={inputClass} value={accountName} onChange={(e) => setAccountName(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <h2 className="text-lg font-semibold mb-4">Next of Kin</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Full Name</label>
                  <input className={inputClass} value={nextOfKinName} onChange={(e) => setNextOfKinName(e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>Phone</label>
                  <input className={inputClass} value={nextOfKinPhone} onChange={(e) => setNextOfKinPhone(e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>Relationship</label>
                  <input
                    className={inputClass}
                    value={nextOfKinRelationship}
                    onChange={(e) => setNextOfKinRelationship(e.target.value)}
                    placeholder="e.g. Spouse, Parent, Sibling"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className={primaryBtn}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </form>

      <form onSubmit={handlePassword} className="bg-white p-6 rounded-lg shadow-lg space-y-4">
        <h2 className="text-lg font-semibold">Change Password</h2>
        {pwMsg && <Alert type={pwMsg.ok ? 'success' : 'error'}>{pwMsg.text}</Alert>}
        <div>
          <label className={labelClass}>Current Password</label>
          <input type="password" className={inputClass} value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} required />
        </div>
        <div>
          <label className={labelClass}>New Password</label>
          <input type="password" className={inputClass} value={pwNew} onChange={(e) => setPwNew(e.target.value)} required minLength={8} placeholder="At least 8 characters" />
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={pwSaving || !pwCurrent || !pwNew} className={primaryBtn}>{pwSaving ? 'Updating...' : 'Update Password'}</button>
        </div>
      </form>
    </div>
  );
}
